import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { camelCase, intersectionWith } from 'lodash';
import React from 'react';
import { FormattedNumber } from 'react-intl';

import { MarketingTag } from '@swf/core/types/CasinoGame';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import { marketingMultipliers } from '@swf/core/utils/casino';

import {
  TXTACL_Hot,
  TXTCLI_High_Roller,
  TXTCLI_LIVE,
  TXTCLI_New__new_game,
  TXTMOB_Exclusive,
  TXTWCL_Race
} from './Ribbon.messages.json';
import styles from './Ribbon.scss';

export interface Props {
  className?: string;
}

const multiplierTags = [...marketingMultipliers].reverse().reduce(
  (acc, val) => {
    acc[val] = val.replace('multiplier', '');
    return acc;
  },
  {} as Record<string, string>
);

const tagI18nByPriority: Record<string, string | TranslationTag> = {
  [MarketingTag.Race]: TXTWCL_Race,
  [MarketingTag.Exclusive]: TXTMOB_Exclusive,
  [MarketingTag.New]: TXTCLI_New__new_game,
  ...multiplierTags,
  [MarketingTag.Hot]: TXTACL_Hot,
  [MarketingTag.HighRoller]: TXTCLI_High_Roller,
  [MarketingTag.LiveLabel]: TXTCLI_LIVE
};

const Ribbon: React.FC<Props & CasinoGame> = ({ tags = [], jackpot, className }) => {
  const [tag] = intersectionWith(Object.keys(tagI18nByPriority), tags, (a, b) => b.startsWith(a));
  const tagIsMultiplier = Object.keys(multiplierTags).includes(tag);
  return tag ? (
    <div
      className={classNames(styles.ribbon, className && styles[className as keyof typeof styles], {
        [styles[camelCase(`${tag}-game`) as keyof typeof styles]]: !tagIsMultiplier,
        [styles.multiplier]: tagIsMultiplier,
        [styles.jackpot]: jackpot
      })}
    >
      {tagIsMultiplier ? (
        <>
          {'x'}
          <FormattedNumber
            style="decimal"
            value={Number(multiplierTags[tag])}
            maximumFractionDigits={0}
            minimumFractionDigits={0}
          />
        </>
      ) : (
        <Translation {...(tagI18nByPriority[tag] as TranslationTag)} />
      )}
    </div>
  ) : null;
};

export default Ribbon;
